import { Fragment, useState } from "react";
import { ChevronLeft, ChevronRight, Check, AlertTriangle } from "lucide-react";
import { PageLoader } from "@/components/ui/Spinner";
import { formatAmount } from "@/utils/formatAmount";
import { useSalaryReconciliation } from "@/features/finances/hooks/useGroupForecast";

const MONTHS_LONG = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

// Słowny opis podstawy rozdziału kosztu obsługi firmy (smaru nieprojektowych) na grupy.
function smearBasisText(b: "occupancy" | "value" | "manual"): string {
  if (b === "manual") return "wg ręcznego udziału CEO";
  if (b === "value") return "wg wartość×aktywność (nikt nieobsadzony w tym miesiącu)";
  return "wg obciążenia kosztowego (Σ % etatu × pensja obsady)";
}

function Money({ value }: { value: string }) {
  return (
    <>
      {formatAmount(value)}{" "}
      <span className="text-xs font-normal text-gray-400">PLN</span>
    </>
  );
}

export function SalaryReconciliationPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const { data, isLoading } = useSalaryReconciliation(year, month);

  function shift(d: number) {
    let ny = year,
      nm = month + d;
    if (nm < 1) {
      ny--;
      nm = 12;
    }
    if (nm > 12) {
      ny++;
      nm = 1;
    }
    setYear(ny);
    setMonth(nm);
  }

  if (isLoading || !data) return <PageLoader />;

  const ok = data.pools_match && data.new.invariant_ok;
  const monthLabel = `${MONTHS_LONG[month - 1]} ${year}`;

  // Sumy liczone na froncie wprost z wierszy tabel (dowód, że pozycje się sumują).
  const oldProjectsTotal = data.old.projects
    .reduce((s, p) => s + parseFloat(p.amount), 0)
    .toFixed(2);
  const oldExplicitTotal = data.old.projects
    .reduce((s, p) => s + parseFloat(p.explicit), 0)
    .toFixed(2);
  const oldSmearTotal = data.old.projects
    .reduce((s, p) => s + parseFloat(p.smear), 0)
    .toFixed(2);
  const newGroupsTotal = data.new.groups
    .reduce((s, g) => s + parseFloat(g.total), 0)
    .toFixed(2);
  const nonProjPeopleTotal = data.new.non_project_people
    .reduce((s, p) => s + parseFloat(p.amount), 0)
    .toFixed(2);
  // Grupy niosące koszt obsługi firmy (do rozkładu puli nieprojektowych).
  const smearGroups = data.new.groups.filter((g) => parseFloat(g.overhead_cost) > 0);

  return (
    <div className="flex flex-col gap-4 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Podział kosztów płac — porównanie
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
            Jak koszty płac rozkładają się dziś (na projekty) i jak rozłożą się
            w nowym ujęciu — na grupy zleceń, z osobno wykazanymi wolnymi
            mocami. Łączna kwota jest taka sama; zmienia się tylko przypisanie.
            Poniżej pełny dowód co do osoby i przydziału — nic nie jest
            schowane.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => shift(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="px-3 font-semibold text-sm text-gray-800 dark:text-gray-100 min-w-[140px] text-center">
            {monthLabel}
          </span>
          <button
            onClick={() => shift(1)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div
        className={`flex items-center gap-2 px-4 py-3 rounded-lg ${ok ? "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300" : "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300"}`}
      >
        {ok ? <Check size={18} /> : <AlertTriangle size={18} />}
        <span className="text-sm font-medium">
          {ok
            ? "Oba ujęcia się bilansują — łączna kwota płac jest taka sama, nic nie ginie po drodze."
            : "Uwaga: kwoty się nie zgadzają — sprawdź dane, zanim oprzesz decyzje na nowym ujęciu."}
        </span>
      </div>

      {/* Headline totals */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 p-5">
          <h2 className="text-sm font-mono uppercase tracking-wider text-gray-500 mb-3">
            Obecny podział (na projekty)
          </h2>
          <Row
            label="Łączne koszty płac"
            value={<Money value={data.old.pool} />}
            bold
          />
          <Row label="Liczba projektów" value={String(data.old.project_rows)} />
        </div>
        <div className="bg-white dark:bg-gray-800 p-5">
          <h2 className="text-sm font-mono uppercase tracking-wider text-gray-500 mb-3">
            Nowy podział (grupy + wolne moce)
          </h2>
          <Row
            label="Łączne koszty płac"
            value={<Money value={data.new.pool} />}
            bold
          />
          <Row
            label="Koszt na grupach zleceń"
            value={<Money value={data.new.groups_total} />}
          />
          <Row
            label="w tym koszt obsługi firmy"
            value={<Money value={data.new.non_project_total} />}
            muted
          />
          <Row
            label="Wolne moce ludzi"
            value={
              <span className="text-amber-700 dark:text-amber-400">
                <Money value={data.new.free_total} />
              </span>
            }
            bold
          />
          <p className="text-xs text-gray-400 mt-2">
            Grupy + wolne moce = łączne płace{" "}
            {data.new.invariant_ok ? "✓" : "✗"}.
          </p>
        </div>
      </div>

      {/* DOWODY — wszystko rozwinięte, każda kwota z jawnym wzorem */}
      <Proof
        title="Dowód: obecny podział — koszt płac na projektach"
        hint="Stary silnik: Kwota = imienne przydziały (Σ % etatu × pełny koszt) + rozsmarowana reszta (niedociążenie + nieprojektowi), rozdzielona po projektach wg wartość×aktywność. Reszta = Kwota − Przydziały (z definicji sumuje się do Kwoty). Kolumny się sumują."
      >
        {data.old.projects.length === 0 ? (
          <p className="text-sm text-gray-400">Brak pozycji w tym miesiącu.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-400 text-left">
                <th className="py-1">Projekt</th>
                <th className="py-1 text-right">Przydziały imienne</th>
                <th className="py-1 text-right">Smar (reszta)</th>
                <th className="py-1 text-right">Kwota</th>
              </tr>
            </thead>
            <tbody>
              {data.old.projects.map((p, i) => (
                <tr
                  key={`${p.name}-${i}`}
                  className="text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700"
                >
                  <td className="py-1.5">{p.name}</td>
                  <td className="py-1.5 text-right tabular-nums text-gray-400">
                    {formatAmount(p.explicit)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-400">
                    {formatAmount(p.smear)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    −{formatAmount(p.amount)} PLN
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold text-gray-900 dark:text-gray-100">
                <td className="py-1.5">SUMA</td>
                <td className="py-1.5 text-right tabular-nums">
                  {formatAmount(oldExplicitTotal)}
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  {formatAmount(oldSmearTotal)}
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  −{formatAmount(oldProjectsTotal)} PLN
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Proof>

      <Proof
        title="Dowód: nowy podział — grupy → projekty → pracownicy"
        hint={`${monthLabel}. Koszt grupy = imienne przydziały (Σ pracowników: % etatu × pełny koszt) + koszt obsługi firmy = pula nieprojektowych ${formatAmount(data.new.non_project_total)} PLN rozdzielona ${smearBasisText(data.new.smear_basis)}. Każdy wiersz ma wzór pod nazwą.`}
      >
        {data.new.groups.length === 0 ? (
          <p className="text-sm text-gray-400">
            Brak grup z kosztem w tym miesiącu.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-400 text-left">
                <th className="py-1">Grupa / projekt / pracownik</th>
                <th className="py-1 text-right">% etatu</th>
                <th className="py-1 text-right">Pełny koszt</th>
                <th className="py-1 text-right">Kwota</th>
              </tr>
            </thead>
            <tbody>
              {data.new.groups.map((g, gi) => (
                <Fragment key={`${g.name}-${gi}`}>
                  <tr className="bg-gray-50 dark:bg-gray-700/40 font-semibold text-gray-800 dark:text-gray-100">
                    <td className="py-1.5 px-1">
                      {g.name}
                      <div className="text-[10px] font-normal text-gray-400">
                        = przydziały {formatAmount(g.project_cost)} + obsługa
                        firmy {formatAmount(g.overhead_cost)}
                      </div>
                    </td>
                    <td></td>
                    <td></td>
                    <td className="py-1.5 px-1 text-right tabular-nums">
                      −{formatAmount(g.total)} PLN
                    </td>
                  </tr>
                  {g.projects.map((p, pi) => (
                    <Fragment key={`${g.name}-p-${pi}`}>
                      <tr className="text-gray-700 dark:text-gray-200 font-medium">
                        <td className="py-1 pl-4">
                          {p.name}{" "}
                          <span className="text-[10px] text-gray-400 font-normal">
                            (przydziały)
                          </span>
                        </td>
                        <td></td>
                        <td></td>
                        <td className="py-1 text-right tabular-nums">
                          −{formatAmount(p.amount)} PLN
                        </td>
                      </tr>
                      {p.employees.map((e, ei) => (
                        <tr
                          key={`${g.name}-p-${pi}-e-${ei}`}
                          className="text-gray-500 dark:text-gray-400"
                        >
                          <td className="py-0.5 pl-8">
                            {e.name}
                            <span className="text-[10px] text-gray-400 ml-1">
                              ({e.fraction_pct.toFixed(1)}% ×{" "}
                              {formatAmount(e.full_cost)})
                            </span>
                          </td>
                          <td className="py-0.5 text-right tabular-nums">
                            {e.fraction_pct.toFixed(1)}%
                          </td>
                          <td className="py-0.5 text-right tabular-nums">
                            {formatAmount(e.full_cost)}
                          </td>
                          <td className="py-0.5 text-right tabular-nums">
                            −{formatAmount(e.amount)} PLN
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  {parseFloat(g.overhead_cost) > 0 && (
                    <tr className="text-gray-500 dark:text-gray-400">
                      <td className="py-1 pl-4">
                        Koszt obsługi firmy{" "}
                        <span className="text-[10px] text-gray-400">
                          (nieprojektowi {smearBasisText(data.new.smear_basis)})
                        </span>
                        <div className="text-[10px] text-gray-400">
                          udział {g.overhead_share.toFixed(1)}% × pula
                          nieprojektowych{" "}
                          {formatAmount(data.new.non_project_total)}
                          {data.new.smear_basis === "occupancy" &&
                            ` · udział = obciążenie grupy ${formatAmount(g.load)} ÷ portfel ${formatAmount(data.new.total_load)}`}
                        </div>
                      </td>
                      <td></td>
                      <td></td>
                      <td className="py-1 text-right tabular-nums">
                        −{formatAmount(g.overhead_cost)} PLN
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold text-gray-900 dark:text-gray-100">
                <td className="py-1.5 px-1">SUMA grup</td>
                <td></td>
                <td></td>
                <td className="py-1.5 px-1 text-right tabular-nums">
                  −{formatAmount(newGroupsTotal)} PLN
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Proof>

      <Proof
        title="Dowód: koszt obsługi firmy — pracownicy nieprojektowi"
        hint={`Pełne pensje nieprojektowych (kadry/księgowość/zarząd) tworzą pulę ${formatAmount(data.new.non_project_total)} PLN. Rozkłada się ona na grupy ${smearBasisText(data.new.smear_basis)}${data.new.smear_basis === "occupancy" ? "; udział grupy = jej obciążenie kosztowe ÷ obciążenie całego portfela" : ""}. Poniżej: najpierw pula (kto ją tworzy), potem jej rozkład na grupy.`}
      >
        {/* 1) Pula: pełne pensje nieprojektowych */}
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
          Pula (pełne pensje nieprojektowych)
        </p>
        {data.new.non_project_people.length === 0 ? (
          <p className="text-sm text-gray-400">
            Brak pracowników nieprojektowych w tym miesiącu.
          </p>
        ) : (
          <table className="w-full text-sm mb-4">
            <tbody>
              {data.new.non_project_people.map((p, i) => (
                <tr
                  key={`${p.name}-${i}`}
                  className="text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700"
                >
                  <td className="py-1.5">{p.name}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    −{formatAmount(p.amount)} PLN
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold text-gray-900 dark:text-gray-100">
                <td className="py-1.5">RAZEM pula</td>
                <td className="py-1.5 text-right tabular-nums">
                  −{formatAmount(nonProjPeopleTotal)} PLN
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {/* 2) Rozkład puli na grupy wg udziału */}
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
          Rozkład puli na grupy ({smearBasisText(data.new.smear_basis)})
        </p>
        {smearGroups.length === 0 ? (
          <p className="text-sm text-gray-400">
            Pula nie trafia na żadną grupę (brak obsady / brak grup).
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-400 text-left">
                <th className="py-1">Grupa</th>
                <th className="py-1 text-right">Udział</th>
                <th className="py-1 text-right">Kwota (udział × pula)</th>
              </tr>
            </thead>
            <tbody>
              {smearGroups.map((g, i) => (
                <tr
                  key={`${g.name}-sm-${i}`}
                  className="text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700"
                >
                  <td className="py-1.5">
                    {g.name}
                    {data.new.smear_basis === "occupancy" && (
                      <span className="text-[10px] text-gray-400 ml-1">
                        (obciążenie {formatAmount(g.load)} ÷ portfel{" "}
                        {formatAmount(data.new.total_load)})
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-400">
                    {g.overhead_share.toFixed(1)}%
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    −{formatAmount(g.overhead_cost)} PLN
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold text-gray-900 dark:text-gray-100">
                <td className="py-1.5">SUMA</td>
                <td className="py-1.5 text-right tabular-nums">100,0%</td>
                <td className="py-1.5 text-right tabular-nums">
                  −{formatAmount(data.new.non_project_total)} PLN
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Proof>

      <Proof
        title="Dowód: wolne moce ludzi"
        hint="Niedociążenie pracowników projektowych: Wolne moce = pełny koszt × (100% − przydzielony). Jawna pozycja — NIE dosypywana do zleceń. Suma na dole."
      >
        {data.new.free_people.length === 0 ? (
          <p className="text-sm text-gray-400">
            Brak wolnych mocy w tym miesiącu.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-400 text-left">
                <th className="py-1">Pracownik</th>
                <th className="py-1 text-right">Pełny koszt</th>
                <th className="py-1 text-right">Przydzielony</th>
                <th className="py-1 text-right">Wolne</th>
                <th className="py-1 text-right">Wolne moce</th>
              </tr>
            </thead>
            <tbody>
              {data.new.free_people.map((p, i) => (
                <tr
                  key={`${p.name}-${i}`}
                  className="text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700"
                >
                  <td className="py-1.5">
                    {p.name}
                    <span className="text-[10px] text-gray-400 ml-1">
                      ({p.free_pct.toFixed(1)}% × {formatAmount(p.full_cost)})
                    </span>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatAmount(p.full_cost)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-400">
                    {p.allocated_pct.toFixed(1)}%
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-400">
                    {p.free_pct.toFixed(1)}%
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-amber-700 dark:text-amber-400">
                    −{formatAmount(p.amount)} PLN
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold text-gray-900 dark:text-gray-100">
                <td className="py-1.5">SUMA</td>
                <td></td>
                <td></td>
                <td></td>
                <td className="py-1.5 text-right tabular-nums text-amber-700 dark:text-amber-400">
                  −{formatAmount(data.new.free_total)} PLN
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Proof>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span
        className={`text-sm ${muted ? "text-gray-400 pl-3" : "text-gray-600 dark:text-gray-300"}`}
      >
        {label}
      </span>
      <span
        className={`tabular-nums ${bold ? "font-bold text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"}`}
      >
        {value}
      </span>
    </div>
  );
}

function Proof({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 p-5">
      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        {title}
      </h2>
      <p className="text-xs text-gray-400 mb-3 mt-0.5 max-w-3xl">{hint}</p>
      {children}
    </div>
  );
}
